import React, { useState } from 'react';
import authService from '../services/authService';
import './AdminInvite.css';

interface AdminInviteProps {
  onInviteSent?: (email: string) => void
}

const AdminInvite: React.FC<AdminInviteProps> = ({ onInviteSent }) => {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'moderator'>('admin')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

    const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Get current user token from localStorage or wherever you store it
      const currentToken = localStorage.getItem('authToken') || '';
      
      if (!currentToken) {
        setError('You must be logged in to invite users');
        return;
      }

      const response = await authService.inviteAdmin(currentToken, email, role);

      if (response.success) {
        setMessage(`Successfully invited ${email} as ${role}. ${response.data?.temporaryPassword ? `Temporary password: ${response.data.temporaryPassword}` : ''}`);
        setEmail('');
        setRole('admin');
      } else {
        setError(response.error || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Invitation error:', err);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setMessage('')
    setError('')
  }

  return (
    <div className="admin-invite-section">
      <h3>Invite Admin User</h3>
      <p>Send an invitation to a new admin user. They will receive an email with setup instructions.</p>
      
      {error && (
        <div className="error-message">
          {error}
          <button onClick={clearMessages} className="message-close">×</button>
        </div>
      )}
      
      {message && (
        <div className="success-message">
          {message}
          <button onClick={clearMessages} className="message-close">×</button>
        </div>
      )}

      <form onSubmit={handleInvite} className="admin-invite-form">
        <div className="form-group">
          <label htmlFor="invite-email">Email Address</label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="invite-role">Role</label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'moderator')}
            disabled={loading}
          >
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="btn btn-primary"
        >
          {loading ? 'Sending...' : 'Send Invitation'}
        </button>
      </form>

      <div className="invite-info">
        <h4>Role Permissions:</h4>
        <ul>
          <li><strong>Admin:</strong> Full access to all features including user management</li>
          <li><strong>Moderator:</strong> Access to event and ticket management</li>
        </ul>
      </div>
    </div>
  )
}

export default AdminInvite
